import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ButtonGroup,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
  AttachMoney,
  Login,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUBLISHABLE_KEY, STRIPE_ENABLED } from '../../../config/stripe';
import { useBooking } from '../../../contexts/BookingContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';

// Extra services mapping for display
const EXTRA_SERVICES = {
  'ceiling-fans': {
    id: 'ceiling-fans',
    title: 'Ceiling Fans',
    description: 'Dust and clean ceiling fans',
    pricePerUnit: 15,
    unit: 'per fan'
  },
  'home-organization': {
    id: 'home-organization',
    title: 'Home Organization',
    description: 'Professional organization services',
    pricePerUnit: 50,
    unit: 'per hour'
  },
  'inside-cabinets': {
    id: 'inside-cabinets',
    title: 'Inside Cabinets',
    description: 'Deep clean cabinet interiors',
    pricePerUnit: 35,
    unit: 'flat rate'
  },
  'inside-windows': {
    id: 'inside-windows',
    title: 'Inside Windows',
    description: 'Clean interior window surfaces',
    pricePerUnit: 25,
    unit: '30 min'
  },
  'inside-fridge': {
    id: 'inside-fridge',
    title: 'Inside the Fridge',
    description: 'Deep clean refrigerator interior',
    pricePerUnit: 40,
    unit: 'flat rate'
  },
  'inside-oven': {
    id: 'inside-oven',
    title: 'Inside the Oven',
    description: 'Deep clean oven interior',
    pricePerUnit: 45,
    unit: 'flat rate'
  },
  'load-of-laundry': {
    id: 'load-of-laundry',
    title: 'Load of Laundry',
    description: 'Wash, dry, and fold laundry',
    pricePerUnit: 30,
    unit: 'loads'
  },
  'wall-cleaning': {
    id: 'wall-cleaning',
    title: 'Wall Cleaning',
    description: 'Clean and wipe walls',
    pricePerUnit: 2,
    unit: 'per sqft'
  },
  'wet-wipe-baseboards': {
    id: 'wet-wipe-baseboards',
    title: 'Wet Wipe Baseboards',
    description: 'Clean and wipe baseboards',
    pricePerUnit: 20,
    unit: 'flat rate'
  },
  'wet-wipe-blinds': {
    id: 'wet-wipe-blinds',
    title: 'Wet Wipe Blinds',
    description: 'Clean and wipe window blinds',
    pricePerUnit: 10,
    unit: 'per blind'
  }
};

// Initialize Stripe (only if enabled)
const stripePromise = STRIPE_ENABLED && STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

// Validation utilities
const validateExtraService = (extraService) => {
  console.log('validateExtraService - Input:', extraService);
  
  if (!extraService || typeof extraService !== 'object') {
    console.warn('validateExtraService - Invalid extra service:', extraService);
    return null;
  }

  if (!extraService.id) {
    console.warn('validateExtraService - Extra service missing ID:', extraService);
    return null;
  }

  // Check if service has complete information (new format)
  if (extraService.name && extraService.rate) {
    // New format: service information is already stored
    let quantity = parseInt(extraService.quantity) || 1;
    if (quantity < 1) {
      console.warn(`validateExtraService - Invalid quantity (${quantity}) for service ${extraService.name}, defaulting to 1`);
      quantity = 1;
    }

    const validated = {
      ...extraService,
      quantity,
      serviceInfo: {
        id: extraService.id,
        title: extraService.name,
        description: extraService.description,
        pricePerUnit: extraService.rate,
        unit: extraService.unit || 'per unit'
      }
    };

    console.log('validateExtraService - Output (new format):', validated);
    return validated;
  }

  // Fallback to old format with hardcoded mapping
  const serviceInfo = EXTRA_SERVICES[extraService.id];
  if (!serviceInfo) {
    console.error(`validateExtraService - Invalid extra service ID: ${extraService.id}`);
    return null;
  }

  let quantity = parseInt(extraService.quantity) || 1;
  if (quantity < 1) {
    console.warn(`validateExtraService - Invalid quantity (${quantity}) for service ${serviceInfo.title}, defaulting to 1`);
    quantity = 1;
  }

  const validated = {
    ...extraService,
    quantity,
    serviceInfo
  };

  console.log('validateExtraService - Output (old format):', validated);
  return validated;
};

// Order Summary Component
const OrderSummaryContent = ({ priceBreakdown, onQuantityChange, onRemoveExtra }) => {
  const { bookingState } = useBooking();
  
  console.log('OrderSummaryContent - Initial render:', {
    bookingState: {
      extraServices: bookingState.extraServices,
      serviceType: bookingState.serviceType,
      serviceName: bookingState.serviceName
    },
    priceBreakdown
  });
  
  // Base service price
  const renderBaseService = () => {
    console.log('renderBaseService - Rendering base service:', {
      serviceName: bookingState.serviceName,
      basePrice: priceBreakdown?.breakdown?.[0]?.amount
    });
    
    return (
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography>
          {bookingState.serviceName || 'General Cleaning'}
        </Typography>
        <Typography>
          ${priceBreakdown?.breakdown?.[0]?.amount?.toFixed(2) || '0.00'}
        </Typography>
      </Stack>
    );
  };

  // Extra services section - consistent display
  const renderExtraServicesSection = () => {
    console.log('renderExtraServicesSection - Before validation:', {
      rawExtraServices: bookingState.extraServices
    });

    if (!Array.isArray(bookingState.extraServices)) {
      console.log('renderExtraServicesSection - extraServices is not an array:', bookingState.extraServices);
      return null;
    }

    const validatedExtraServices = bookingState.extraServices
      .filter(extra => extra && typeof extra === 'object')
      .map(extraService => {
        const validated = validateExtraService(extraService);
        console.log('Validating extra service:', {
          original: extraService,
          validated: validated,
          serviceInfo: extraService?.id ? EXTRA_SERVICES[extraService.id] : null
        });
        return validated;
      })
      .filter(Boolean);

    console.log('renderExtraServicesSection - After validation:', {
      validatedExtraServices,
      count: validatedExtraServices.length
    });

    if (!validatedExtraServices.length) {
      console.log('No valid extra services found');
      return null;
    }
    
    return (
      <>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
          Additional Services
        </Typography>
        {validatedExtraServices.map((extraService, index) => {
          const { serviceInfo, quantity } = extraService;
          const totalPrice = serviceInfo.pricePerUnit * quantity;

          console.log(`Rendering extra service ${index}:`, {
            title: serviceInfo.title,
            quantity,
            unitPrice: serviceInfo.pricePerUnit,
            totalPrice
          });

          return (
            <Box key={index}>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {/* Service name and total price row */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography>
                    {serviceInfo.title}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography fontWeight="medium">
                      ${totalPrice.toFixed(2)}
                    </Typography>
                    {onRemoveExtra && (
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => onRemoveExtra(extraService.id)}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
                
                {/* Quantity controls row */}
                {onQuantityChange && (
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      ${serviceInfo.pricePerUnit.toFixed(2)} {serviceInfo.unit}
                    </Typography>
                    <ButtonGroup size="small" variant="outlined">
                      <IconButton
                        size="small"
                        onClick={() => onQuantityChange(extraService.id, Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <TextField
                        size="small"
                        value={quantity}
                        onChange={(e) => {
                          const newQty = parseInt(e.target.value) || 1;
                          if (newQty >= 1 && newQty <= 999) {
                            onQuantityChange(extraService.id, newQty);
                          }
                        }}
                        inputProps={{
                          style: { textAlign: 'center', width: '40px', padding: '4px 8px' },
                          min: 1,
                          max: 999
                        }}
                        variant="outlined"
                      />
                      <IconButton
                        size="small"
                        onClick={() => onQuantityChange(extraService.id, Math.min(999, quantity + 1))}
                        disabled={quantity >= 999}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </ButtonGroup>
                  </Stack>
                )}
              </Stack>
              {index < validatedExtraServices.length - 1 && (
                <Divider sx={{ my: 1, opacity: 0.3 }} />
              )}
            </Box>
          );
        })}
      </>
    );
  };

  return (
    <>
      {renderBaseService()}
      {renderExtraServicesSection()}
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography>
          Subtotal
        </Typography>
        <Typography>
          ${priceBreakdown?.subtotal?.toFixed(2) || '0.00'}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography>
          Tax
        </Typography>
        <Typography>
          ${priceBreakdown?.tax?.toFixed(2) || '0.00'}
        </Typography>
      </Stack>
      <Divider sx={{ mb: 2 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">
          Total
        </Typography>
        <Typography variant="h6">
          ${priceBreakdown?.total?.toFixed(2) || '0.00'}
        </Typography>
      </Stack>

      {/* Service Details Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="body2" color="success.main" gutterBottom>
          Service Details:
        </Typography>
        <List dense>
          <ListItem disablePadding>
            <ListItemText 
              primary={<Typography variant="body2">Service</Typography>}
              secondary={<Typography variant="body2" color="success.main">{bookingState.serviceName || bookingState.serviceType}</Typography>}
            />
          </ListItem>
          <ListItem disablePadding>
            <ListItemText 
              primary={<Typography variant="body2">Property</Typography>}
              secondary={
                <Typography variant="body2" color="success.main">
                  {`${bookingState.propertyDetails.address}, ${bookingState.propertyDetails.city}, ${bookingState.propertyDetails.zipCode}`}
                </Typography>
              }
            />
          </ListItem>
          <ListItem disablePadding>
            <ListItemText 
              primary={<Typography variant="body2">Date & Time</Typography>}
              secondary={
                <Typography variant="body2" color="success.main">
                  {bookingState.scheduling.flexibleDates 
                    ? "We'll contact you to schedule"
                    : new Date(bookingState.scheduling.serviceDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }) + ` at ${bookingState.scheduling.serviceTime}`
                  }
                </Typography>
              }
            />
          </ListItem>
        </List>
      </Box>

      {/* Customer Information Section */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="success.main" gutterBottom>
          Customer Information:
        </Typography>
        <List dense>
          <ListItem disablePadding>
            <ListItemText 
              primary={<Typography variant="body2">Contact</Typography>}
              secondary={
                <Typography variant="body2" color="success.main">
                  {`${bookingState.customerInfo.firstName} ${bookingState.customerInfo.lastName}`}
                </Typography>
              }
            />
          </ListItem>
          <ListItem disablePadding>
            <ListItemText 
              primary={<Typography variant="body2">Email</Typography>}
              secondary={
                <Typography variant="body2" color="success.main">
                  {bookingState.customerInfo.email}
                </Typography>
              }
            />
          </ListItem>
          <ListItem disablePadding>
            <ListItemText 
              primary={<Typography variant="body2">Phone</Typography>}
              secondary={
                <Typography variant="body2" color="success.main">
                  {bookingState.customerInfo.phone}
                </Typography>
              }
            />
          </ListItem>
        </List>
      </Box>
    </>
  );
};

function PaymentForm({ onPaymentComplete }) {
  const stripe = useStripe();
  const elements = useElements();
  const { user, clientProfile, isReturningCustomer } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    bookingState,
    updatePayment,
    updateOrderID,
    setSubmitting,
    clearErrors,
    markStepCompleted,
    setCurrentStep,
    setExtraServices,
    // Booking methods
    saveAsBooking,
    commitBooking,
    // Helper functions
    getAvailableSteps
  } = useBooking();

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [saveCardConsent, setSaveCardConsent] = useState(false);

  // Check feature flag for credit card payment
  const isCreditCardPaymentEnabled = process.env.REACT_APP_FEATUREFLIP_CC_PAYMENT === '1';

  // Recurring bookings save the card and auto-charge each cycle (per invoice terms)
  const isRecurringBooking = Boolean(
    bookingState.scheduling?.frequency && bookingState.scheduling.frequency !== 'one-time'
  );

  const calculatePricing = async (currentState) => {
    if (!currentState) {
      console.error('calculatePricing - No state provided');
      return;
    }

    console.log('calculatePricing - Starting calculation with state:', {
      state: {
        serviceType: currentState.serviceType,
        extraServices: currentState.extraServices,
        propertyDetails: currentState.propertyDetails,
        scheduling: currentState.scheduling
      }
    });

    try {
      const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
      const tenantId = process.env.REACT_APP_TENANT_ID || '1';
      const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';
      
      // Validate extra services before sending to API
      const validatedExtraServices = Array.isArray(currentState.extraServices)
        ? currentState.extraServices
            .filter(extra => extra && typeof extra === 'object')
            .map(validateExtraService)
            .filter(Boolean)
        : [];
      
      console.log('calculatePricing - Validated extra services:', validatedExtraServices);

      // Call pricing API
      const requestBody = {
        mainServiceId: currentState.serviceType,
        propertyDetails: currentState.propertyDetails,
        scheduling: currentState.scheduling,
        extraServices: Array.isArray(currentState.extraServices) 
          ? currentState.extraServices.map(extra => ({
              catalogID: extra.id,
              quantity: extra.quantity !== undefined ? extra.quantity : 1
            }))
          : [],
        tenantId: parseInt(tenantId),
        cityCode
      };

      console.log('calculatePricing - API request:', {
        url: `${apiBase}/api/bookings/calculate-price`,
        body: requestBody,
        extraServicesDebug: currentState.extraServices?.map(extra => ({
          id: extra.id,
          name: extra.name,
          originalQuantity: extra.quantity,
          payloadQuantity: extra.quantity !== undefined ? extra.quantity : 1
        }))
      });

      const response = await fetch(`${apiBase}/api/bookings/calculate-price`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('calculatePricing - API response status:', response.status);

      if (response.ok) {
        const pricing = await response.json();
        console.log('calculatePricing - API pricing response:', pricing);
        
        // Ensure extra services are properly included in the breakdown
        const extraServicesBreakdown = validatedExtraServices.map(extraService => {
          const { serviceInfo, quantity } = extraService;
          const amount = serviceInfo.pricePerUnit * quantity;
          
          return {
            label: serviceInfo.title,
            amount,
            quantity,
            unitPrice: serviceInfo.pricePerUnit,
            unit: serviceInfo.unit
          };
        });

        console.log('calculatePricing - Extra services breakdown:', extraServicesBreakdown);

        // Combine API pricing with local extra services calculation
        const updatedPricing = {
          ...pricing,
          breakdown: [
            ...(pricing.breakdown || []).filter(item => 
              !validatedExtraServices.some(extra => extra.serviceInfo.title === item.label)
            ),
            ...extraServicesBreakdown
          ]
        };

        // Recalculate totals
        updatedPricing.subtotal = updatedPricing.breakdown.reduce((total, item) => total + item.amount, 0);
        updatedPricing.tax = updatedPricing.subtotal * 0.08;
        updatedPricing.total = updatedPricing.subtotal + updatedPricing.tax;

        console.log('calculatePricing - Final pricing:', updatedPricing);
        
        // Store what this price was calculated for
        const pricingWithMetadata = {
          ...updatedPricing,
          lastCalculatedFor: {
            serviceType: currentState.serviceType,
            propertyDetails: JSON.stringify(currentState.propertyDetails),
            scheduling: JSON.stringify(currentState.scheduling),
            extraServices: JSON.stringify(currentState.extraServices)
          }
        };
        
        setPriceBreakdown(pricingWithMetadata);
        updatePayment({ 
          estimatedPrice: updatedPricing.subtotal,
          finalPrice: updatedPricing.total 
        });
      } else {
        console.warn('calculatePricing - API request failed, using fallback calculation');
        // Fallback calculation with clear separation
        const basePrice = 150; // Default price
        const validatedExtraServices = Array.isArray(currentState.extraServices)
          ? currentState.extraServices
              .filter(extra => extra && typeof extra === 'object')
              .map(validateExtraService)
              .filter(Boolean)
          : [];

        const extraServicesBreakdown = validatedExtraServices.map(extraService => {
          const { serviceInfo, quantity } = extraService;
          const amount = serviceInfo.pricePerUnit * quantity;
          
          return {
            label: serviceInfo.title,
            amount,
            quantity,
            unitPrice: serviceInfo.pricePerUnit,
            unit: serviceInfo.unit
          };
        });

        const extraServicesTotal = extraServicesBreakdown.reduce((total, service) => total + service.amount, 0);
        const subtotal = basePrice + extraServicesTotal;
        const tax = subtotal * 0.08;
        const total = subtotal + tax;
        
        const fallbackPricing = {
          subtotal,
          tax,
          total,
          breakdown: [
            { label: currentState.serviceName || 'Cleaning Service', amount: basePrice },
            ...extraServicesBreakdown
          ],
          lastCalculatedFor: {
            serviceType: currentState.serviceType,
            propertyDetails: JSON.stringify(currentState.propertyDetails),
            scheduling: JSON.stringify(currentState.scheduling),
            extraServices: JSON.stringify(currentState.extraServices)
          }
        };
        
        console.warn('calculatePricing - Using fallback calculation:', fallbackPricing);
        setPriceBreakdown(fallbackPricing);
        updatePayment({ 
          estimatedPrice: subtotal,
          finalPrice: total 
        });
      }
    } catch (error) {
      console.error('calculatePricing - Error:', error);
      // Use same fallback logic as above
      const basePrice = 150;
      const validatedExtraServices = Array.isArray(currentState.extraServices)
        ? currentState.extraServices
            .filter(extra => extra && typeof extra === 'object')
            .map(validateExtraService)
            .filter(Boolean)
        : [];

      const extraServicesBreakdown = validatedExtraServices.map(extraService => {
        const { serviceInfo, quantity } = extraService;
        const amount = serviceInfo.pricePerUnit * quantity;
        
        return {
          label: serviceInfo.title,
          amount,
          quantity,
          unitPrice: serviceInfo.pricePerUnit,
          unit: serviceInfo.unit
        };
      });

      const extraServicesTotal = extraServicesBreakdown.reduce((total, service) => total + service.amount, 0);
      const subtotal = basePrice + extraServicesTotal;
      const tax = subtotal * 0.08;
      const total = subtotal + tax;
      
      const fallbackPricing = {
        subtotal,
        tax,
        total,
        breakdown: [
          { label: currentState.serviceName || 'Cleaning Service', amount: basePrice },
          ...extraServicesBreakdown
        ],
        lastCalculatedFor: {
          serviceType: currentState.serviceType,
          propertyDetails: JSON.stringify(currentState.propertyDetails),
          scheduling: JSON.stringify(currentState.scheduling),
          extraServices: JSON.stringify(currentState.extraServices)
        }
      };
      
      console.warn('calculatePricing - Using fallback calculation due to error:', fallbackPricing);
      setPriceBreakdown(fallbackPricing);
      updatePayment({ 
        estimatedPrice: subtotal,
        finalPrice: total 
      });
    }
  };

  // Create a ref to hold the debounced function
  const debouncedCalculatePriceRef = useRef(
    debounce((state) => {
      // Create a clean snapshot with only the needed properties
      const stateSnapshot = {
        serviceType: state.serviceType,
        serviceName: state.serviceName,
        extraServices: state.extraServices,
        propertyDetails: state.propertyDetails,
        scheduling: state.scheduling
      };
      console.log('Debounced calculate price called with state snapshot:', stateSnapshot);
      calculatePricing(stateSnapshot);
    }, 500)
  );

  // Calculate pricing
  useEffect(() => {
    const currentBookingState = bookingState; // Capture current state in closure

    // Skip calculation if payment is completed
    if (currentBookingState.payment.bookingCreated) {
      console.log('Skipping price calculation - payment completed');
      return;
    }

    // Create a clean snapshot for comparison
    const currentStateSnapshot = {
      serviceType: currentBookingState.serviceType,
      serviceName: currentBookingState.serviceName,
      propertyDetails: currentBookingState.propertyDetails,
      scheduling: currentBookingState.scheduling,
      extraServices: currentBookingState.extraServices
    };
    
    // Skip if we already have a price and nothing relevant changed
    if (priceBreakdown?.lastCalculatedFor) {
      const lastState = {
        serviceType: priceBreakdown.lastCalculatedFor.serviceType,
        propertyDetails: JSON.parse(priceBreakdown.lastCalculatedFor.propertyDetails || '{}'),
        scheduling: JSON.parse(priceBreakdown.lastCalculatedFor.scheduling || '{}'),
        extraServices: JSON.parse(priceBreakdown.lastCalculatedFor.extraServices || '[]')
      };

      const hasChanged = 
        lastState.serviceType !== currentStateSnapshot.serviceType ||
        JSON.stringify(lastState.propertyDetails) !== JSON.stringify(currentStateSnapshot.propertyDetails) ||
        JSON.stringify(lastState.scheduling) !== JSON.stringify(currentStateSnapshot.scheduling) ||
        JSON.stringify(lastState.extraServices) !== JSON.stringify(currentStateSnapshot.extraServices);

      if (!hasChanged) {
        console.log('Skipping price calculation - no relevant changes');
        return;
      }
    }

    console.log('Triggering debounced price calculation with current state snapshot');
    debouncedCalculatePriceRef.current(currentStateSnapshot);

    // Cleanup function
    return () => {
      console.log('Cleaning up debounced calculation');
      if (debouncedCalculatePriceRef.current?.cancel) {
        debouncedCalculatePriceRef.current.cancel();
      }
    };
  }, [
    bookingState.serviceType,
    bookingState.serviceName,
    bookingState.propertyDetails,
    bookingState.scheduling,
    bookingState.extraServices,
    bookingState.payment.bookingCreated
  ]);

  // Authentication check UI
  if (!user) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Authentication Required
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Please log in or create an account to complete your booking
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          Your booking details will be saved while you log in
        </Alert>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={() => navigate('/login', { state: { from: location.pathname } })}
            startIcon={<Login />}
          >
            Log In
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/register', { state: { from: location.pathname } })}
          >
            Create Account
          </Button>
        </Stack>
      </Box>
    );
  }

  const createBookingInDatabase = async (paymentIntentId) => {
    try {
      const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const bookingData = {
        serviceType: bookingState.serviceType,
        serviceName: bookingState.serviceName,
        cleaningOption: bookingState.cleaningOption,
        extraServices: bookingState.extraServices,
        propertyDetails: bookingState.propertyDetails,
        scheduling: bookingState.scheduling,
        customerInfo: bookingState.customerInfo,
        payment: {
          ...bookingState.payment,
          paymentIntentId: paymentIntentId
        },
        paymentStatus: 'PAID',
        stripePaymentIntentId: paymentIntentId
      };

      const response = await fetch(`${apiBase}/api/bookings/create-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bookingData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create booking');
      }

      const booking = await response.json();
      console.log('✅ Booking created successfully:', booking.id);
      return booking;
    } catch (error) {
      console.error('❌ Error creating booking:', error);
      throw error;
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setSubmitting(true);
    setPaymentError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
      
      // Step 1: Create payment intent on backend.
      // Recurring bookings save the card (setup_future_usage) for the agreed
      // automatic charges — requires the JWT so the card attaches to the
      // customer's reusable Stripe profile.
      const createIntentResponse = await fetch(`${apiBase}/api/payments/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {})
        },
        body: JSON.stringify({
          saveCard: isRecurringBooking,
          amount: (() => {
            const totalAmount = priceBreakdown?.total || bookingState.payment.finalPrice || 150;
            const centsAmount = Math.round(totalAmount * 100);
            console.log('💰 Payment amount calculation:', {
              priceBreakdownTotal: priceBreakdown?.total,
              bookingPaymentFinalPrice: bookingState.payment.finalPrice,
              fallbackAmount: 150,
              selectedAmount: totalAmount,
              centsAmount: centsAmount,
              dollarsDisplay: `${totalAmount.toFixed(2)}`
            });
            return centsAmount;
          })(), // Convert dollars to cents for Stripe
          currency: 'usd',
          description: `${bookingState.serviceName || 'Cleaning Service'} - Booking`,
          metadata: {
            serviceType: bookingState.serviceType,
            customerEmail: bookingState.customerInfo.email,
            bookingId: `temp_${Date.now()}`
          }
        }),
      });

      if (!createIntentResponse.ok) {
        throw new Error('Failed to create payment intent');
      }

      const { clientSecret, paymentIntentId } = await createIntentResponse.json();

      // Step 2: Confirm the payment with Stripe
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${bookingState.customerInfo.firstName} ${bookingState.customerInfo.lastName}`,
            email: bookingState.customerInfo.email,
            phone: bookingState.customerInfo.phone,
            address: {
              line1: bookingState.propertyDetails.address,
              city: bookingState.propertyDetails.city,
              state: bookingState.propertyDetails.state,
              postal_code: bookingState.propertyDetails.zipCode
            }
          }
        },
      });

      if (result.error) {
        setPaymentError(result.error.message);
        setIsSubmitting(false);
        setSubmitting(false);
      } else {
        // Check if payment was successful
        if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
          console.log('✅ Payment confirmed by Stripe:', result.paymentIntent.id);
          
          // Step 3: Verify payment status on backend
          const confirmResponse = await fetch(`${apiBase}/api/payments/confirm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              paymentIntentId: paymentIntentId
            }),
          });

          if (!confirmResponse.ok) {
            throw new Error('Failed to confirm payment');
          }

          const confirmResult = await confirmResponse.json();
          
          if (confirmResult.success) {
            console.log('🎉 Payment confirmed successfully!');
            
            // Update payment info immediately after payment success
            updatePayment({ 
              method: 'card',
              paymentStatus: 'paid',
              paymentIntentId: paymentIntentId,
              paymentResult: confirmResult,
              // Preserve existing calculated prices
              estimatedPrice: bookingState.payment.estimatedPrice,
              finalPrice: bookingState.payment.finalPrice
            });
            
            // Mark payment step as completed and advance to confirmation IMMEDIATELY after payment success
            console.log('📍 PAYMENT: Advancing to confirmation after payment success');
            markStepCompleted(bookingState.currentStep);
            const targetStep = bookingState.currentStep + 1;
            console.log('🎯 PAYMENT: Calling setCurrentStep with:', targetStep);
            setCurrentStep(targetStep);
            
            // Step 4: Handle booking operations asynchronously (don't block confirmation display)
            (async () => {
              try {
                let booking;
                
                // Using unified 6-step flow (booking-state system)
                console.log('🔄 Using unified booking flow (booking-state system)');
                
                // Ensure booking is saved and commit it
                if (!bookingState.bookingId) {
                  const saveResult = await saveAsBooking();
                  if (!saveResult.success) {
                    throw new Error('Failed to save booking before payment');
                  }
                }
                
                // Commit the booking with payment information
                const commitResult = await commitBooking(paymentIntentId);
                
                if (!commitResult.success) {
                  throw new Error(commitResult.error || 'Failed to commit booking');
                }
                
                booking = commitResult.booking;
                console.log('✅ Unified flow: Booking committed successfully', booking);
                
                // Update OrderID at root level (not in payment object)
                if (updateOrderID && typeof updateOrderID === 'function') {
                  updateOrderID(booking.OrderID || booking.orderId || booking.id);
                }
                
                // Update payment with booking info
                updatePayment({ 
                  method: 'card',
                  paymentStatus: 'paid',
                  paymentIntentId: paymentIntentId,
                  paymentResult: confirmResult,
                  bookingId: booking.id,
                  bookingCreated: true,
                  // Preserve existing calculated prices
                  estimatedPrice: bookingState.payment.estimatedPrice,
                  finalPrice: bookingState.payment.finalPrice
                });
                
                console.log('🎉 Payment and booking completed successfully!');
                
                // Call completion callback if provided (for dialog context)
                if (onPaymentComplete) {
                  onPaymentComplete(booking);
                }
              } catch (bookingError) {
                console.error('⚠️ Payment succeeded but booking creation failed:', bookingError);
                // Payment went through but the booking record didn't save.
                // Be honest on the confirmation screen instead of pretending all is
                // well — the Stripe webhook reconciliation alerts an admin, who
                // completes the booking manually.
                updatePayment({
                  bookingOperationStatus: 'failed',
                  bookingOperationFailed: true,
                  bookingOperationError:
                    'Your payment was received, but we hit a snag finalizing your booking. ' +
                    'No action is needed — our team has been notified and will confirm your booking by email shortly.'
                });
              }
            })();
            
            setIsSubmitting(false);
            setSubmitting(false);
            // Payment successful - the confirmation step will handle next steps
          } else {
            setPaymentError(confirmResult.error || 'Payment failed');
            setIsSubmitting(false);
            setSubmitting(false);
          }
        } else {
          setPaymentError(`Payment status: ${result.paymentIntent?.status || 'unknown'}. Please try again.`);
          setIsSubmitting(false);
          setSubmitting(false);
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setPaymentError(err.message || 'Payment failed. Please try again.');
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  // Handlers for order summary interactions
  const handleQuantityChange = (extraId, newQuantity) => {
    console.log('handleQuantityChange:', { 
      extraId, 
      newQuantity, 
      currentExtras: bookingState.extraServices?.map(e => ({ id: e.id, quantity: e.quantity }))
    });
    
    const updatedExtras = bookingState.extraServices.map(extra => 
      extra.id === extraId 
        ? { ...extra, quantity: newQuantity }
        : extra
    );
    
    console.log('handleQuantityChange - updatedExtras:', updatedExtras.map(e => ({ id: e.id, quantity: e.quantity })));
    setExtraServices(updatedExtras);
  };

  const handleRemoveExtra = (extraId) => {
    console.log('handleRemoveExtra:', { extraId });
    
    const updatedExtras = bookingState.extraServices.filter(extra => extra.id !== extraId);
    setExtraServices(updatedExtras);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Payment Information
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Complete your payment to confirm your cleaning service booking
      </Typography>
      
      <Grid container spacing={3}>
        {/* Payment Form */}
        <Grid item xs={12} md={8}>
          {isCreditCardPaymentEnabled ? (
            <form onSubmit={handlePayment}>
              <Stack spacing={3}>
                {/* Stripe Card Element */}
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                      <CreditCardIcon color="primary" />
                      <Typography variant="h6">
                        Payment Information
                      </Typography>
                    </Stack>
                    
                    <Typography variant="body2" sx={{ mb: 2 }}>Card Details</Typography>
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#424770',
                            '::placeholder': { color: '#aab7c4' },
                            padding: '10px 12px',
                          },
                          invalid: { color: '#c23d4b' },
                        },
                      }}
                      onChange={e => setCardComplete(e.complete)}
                    />
                    
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
                      <LockIcon color="success" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        Secure payment powered by Stripe
                      </Typography>
                    </Stack>

                    {/* Recurring service: saved-card consent (required) */}
                    {isRecurringBooking && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={saveCardConsent}
                              onChange={(e) => setSaveCardConsent(e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Save my card for my <strong>{bookingState.scheduling.frequency}</strong> recurring
                              service. I authorize automatic charges of{' '}
                              <strong>${priceBreakdown?.total?.toFixed(2) || '—'}</strong> per visit, processed
                              24 hours before or after each visit as agreed in my service terms. I can pause or
                              cancel anytime by contacting support.
                            </Typography>
                          }
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Payment Error — card was NOT charged; details are kept so
                    the customer can fix the card and press Pay again */}
                {paymentError && (
                  <Alert severity="error">
                    <Typography variant="body2" fontWeight={600}>
                      {paymentError}
                    </Typography>
                    <Typography variant="body2">
                      Your card was not charged. Please check your card details and press Pay again,
                      or try a different card.
                    </Typography>
                  </Alert>
                )}

                {/* Submit Button */}
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting || !cardComplete || !stripe || (isRecurringBooking && !saveCardConsent)}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <CreditCardIcon />}
                  size="large"
                  fullWidth
                >
                  {isSubmitting 
                    ? 'Processing Payment...' 
                    : `Pay ${priceBreakdown?.total?.toFixed(2) || '0.00'}`
                  }
                </Button>
              </Stack>
            </form>
          ) : (
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
                  <PhoneIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  <Typography variant="h6" align="center">
                    Call Us to Book
                  </Typography>                 
                  <Typography variant="body1" color="text.secondary" align="center">
                    To complete your booking, please call us directly at:
                  </Typography>
                  <Typography 
                    variant="h5" 
                    color="primary.main" 
                    component="a"
                    href={`tel:${tenant?.clientSupportPhone || ''}`}
                    sx={{ 
                      fontWeight: 'bold',
                      textDecoration: 'none', 
                      '&:hover': { textDecoration: 'underline' } 
                    }}
                  >
                    {tenant?.clientSupportPhone || 'Contact us'}
                  </Typography>
                  {bookingState.OrderID && (
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        mt: 2, 
                        p: 2, 
                        bgcolor: 'primary.light', 
                        color: 'secondary.contrastText', 
                        borderRadius: 1, 
                        textAlign: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      Reference Code: {bookingState.OrderID}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
                    Our team will help you finalize your booking and process payment over the phone. 
                    {bookingState.OrderID && ' Please mention your reference code when calling.'}
                    {!bookingState.OrderID && ' We\'re here to make the process as smooth as possible!'}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  Order Summary
                </Typography>
                
              </Box>
              
              <OrderSummaryContent 
                priceBreakdown={priceBreakdown} 
                onQuantityChange={handleQuantityChange}
                onRemoveExtra={handleRemoveExtra}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

const PaymentStep = ({ onPaymentComplete }) => {
  if (!STRIPE_ENABLED) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Payment System Disabled
          </Typography>
          <Typography variant="body1">
            Stripe payments are currently disabled. Please contact support for payment processing.
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => onPaymentComplete && onPaymentComplete({ success: true, method: 'manual' })}
          sx={{ mt: 2 }}
        >
          Continue Without Payment
        </Button>
      </Box>
    );
  }

  if (!stripePromise) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Payment System Configuration Error
          </Typography>
          <Typography variant="body1">
            Unable to initialize payment system. Please check configuration.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm onPaymentComplete={onPaymentComplete} />
    </Elements>
  );
};

export default PaymentStep;